import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utlis/axiosInstance";
import { useTheme } from "../utlis/useTheme";
import { useUser } from "../provider/UserProvider";
import XLSX from "sheetjs-style";
import { format, formatDistanceToNow } from "date-fns";
import DeviceUserConnectionsForm from "../components/DeviceUserConnectionsForm";
import { PulseLoader } from "react-spinners";
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useTheme as useMuiTheme,
  alpha,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  Autocomplete,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { Speed as SpeedIcon } from "@mui/icons-material";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import {
  LocalGasStation as FuelIcon,
  Power as PowerIcon,
  Wifi as WifiIcon,
  Battery20 as Battery20Icon,
  Battery30 as Battery30Icon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Clear as ClearIcon,
  VpnKey as VpnKeyIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  NotificationsActive as NotificationsActiveIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";

interface Device {
  id: number;
  name: string;
  unique_id: string;
  status: string;
  phone: string;
  model: string;
  category: string;
  disabled?: boolean;
  sim_number?: string;
  activated_on?: string;
  expiration_time?: string;
  created_at?: string;
  updated_at?: string;
  speed_limit?: number;
  attributes: {
    ignition: boolean;
    charge: boolean;
    blocked: boolean;
    battery_level: string;
    status: string;
    device_online: boolean;
    latest_speed?: number;
    mdn_number?: string;
    customer_name?: string;
    last_updated_at?: number;
  };
  latest_position?: {
    gps_data: {
      latitude: number;
      longitude: number;
      address?: string | null;
    };
    updated_at: number;
  };
  latest_heartbeat?: {
    gps_data: {
      voltage_level?: string | number;
      external_voltage_value?: number;
      terminal_info?: {
        oil_electricity_disconnected: boolean;
        alarm_status?: string;
      };
      battery_power?: number;
    };
    updated_at: number;
  };
}

interface Statistics {
  all: number;
  running: number;
  idle: number;
  stopped: number;
  inactive: number;
  no_data: number;
  expired: number;
}

interface Geofencing {
  id: string;
  name: string;
  description: string;
  type: "circle" | "polygon" | "linestring";
  area: string;
}

const DeviceCards: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } =
    useTheme();
  const { permissions, user } = useUser();
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  // const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [geofencingModalOpen, setGeofencingModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [geofencings, setGeofencings] = useState<Geofencing[]>([]);
  const [selectedGeofencing, setSelectedGeofencing] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const [speedLimitModalOpen, setSpeedLimitModalOpen] = useState(false);
  const [speedLimitDevice, setSpeedLimitDevice] = useState<Device | null>(null);
  const [tempSpeedLimit, setTempSpeedLimit] = useState("");
  const [isSpeedLimitLoading, setIsSpeedLimitLoading] = useState(false);
  const [userConnectionsModalOpen, setUserConnectionsModalOpen] =
    useState(false);
  const [
    selectedDeviceForUserConnections,
    setSelectedDeviceForUserConnections,
  ] = useState<Device | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [selectedDeviceForNotification, setSelectedDeviceForNotification] =
    useState<Device | null>(null);
  const [notificationOptions, setNotificationOptions] = useState<
    Array<{ id: number; label: string }>
  >([]);
  const [notificationDialogLoading, setNotificationDialogLoading] =
    useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState("");
  const [notificationDialogMessage, setNotificationDialogMessage] = useState<{
    type: "success" | "error" | null;
    text: string;
  }>({
    type: null,
    text: "",
  });
  const [attachedNotifications, setAttachedNotifications] = useState<
    Array<any>
  >([]);
  const [attachedNotificationsLoading, setAttachedNotificationsLoading] =
    useState(false);
  const { filter } = useParams<{ filter?: string }>();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();

  // Permission check for export - admins can always export
  const isAdmin = user?.user?.administrator;
  const canExport = isAdmin || permissions?.includes("EXPORT_ACCESS");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Popover for Bulk filters (activation/expiration date ranges)
  const [bulkAnchorEl, setBulkAnchorEl] = useState<HTMLElement | null>(null);
  const [createdFrom, setCreatedFrom] = useState<string>("");
  const [createdTo, setCreatedTo] = useState<string>("");

  const handleOpenBulkPopover = (event: React.MouseEvent<HTMLElement>) => {
    setBulkAnchorEl(event.currentTarget);
  };

  const handleCloseBulkPopover = () => {
    setBulkAnchorEl(null);
  };

  // Helper function to get filter from URL parameter
  const getFilterFromUrl = (urlFilter?: string): string => {
    if (!urlFilter) return "Total";

    const validFilters = [
      "running",
      "idle",
      "stopped",
      "inactive",
      "expired",
      "no-data",
    ];
    const filterMap: { [key: string]: string } = {
      running: "Running",
      idle: "Idle",
      stopped: "Stopped",
      inactive: "Inactive",
      expired: "Expired",
      "no-data": "No Data",
    };

    return validFilters.includes(urlFilter.toLowerCase())
      ? filterMap[urlFilter.toLowerCase()]
      : "Total";
  };

  // Helper function to get URL parameter from filter
  const getUrlFromFilter = (filterName: string): string => {
    const filterMap: { [key: string]: string } = {
      Running: "running",
      Idle: "idle",
      Stopped: "stopped",
      Inactive: "inactive",
      Expired: "expired",
      "No Data": "no-data",
    };

    return filterMap[filterName] || "";
  };

  const [selectedFilter, setSelectedFilter] = useState(
    getFilterFromUrl(filter),
  );

  const getStatusParam = (filterName: string): string | undefined => {
    if (filterName === "Total") return undefined;
    const filterMap: { [key: string]: string } = {
      Running: "running",
      Idle: "idle",
      Stopped: "stopped",
      Inactive: "inactive",
      Expired: "expired",
      "No Data": "no_data",
    };
    return filterMap[filterName];
  };

  // Handle row click to navigate to vehicle tracking page
  const handleRowClick = (deviceId: number) => {
    navigate(`/vehicle-tracking/${deviceId}`);
  };

  // Handle opening geofencing modal
  // const handleGeofencingClick = (event: React.MouseEvent, device: Device) => {
  //   event.stopPropagation(); // Prevent row click
  //   setSelectedDevice(device);
  //   setGeofencingModalOpen(true);
  // };

  // Handle closing geofencing modal
  const handleCloseGeofencingModal = () => {
    setGeofencingModalOpen(false);
    setSelectedDevice(null);
    setSelectedGeofencing("");
  };

  // Handle adding geofencing to device
  const handleAddGeofencing = async () => {
    if (!selectedDevice || !selectedGeofencing) {
      return;
    }

    try {
      const response = await axiosInstance.post(
        "/gpstracker/configurationhandler/",
        {
          device_id: selectedDevice.id,
          geofence_id: selectedGeofencing,
        },
      );

      if (response.data.status) {
        console.log("Geofencing added successfully:", response.data);
        // Close modal and reset selections
        handleCloseGeofencingModal();
        // You can add a success notification here if needed
      } else {
        console.error("Failed to add geofencing:", response.data.message);
        // You can add error notification here if needed
      }
    } catch (err: any) {
      console.error("Error adding geofencing:", err);
      // You can add error notification here if needed
    }
  };

  // Handle edit device click
  const handleEditDevice = (event: React.MouseEvent, device: Device) => {
    event.stopPropagation(); // Prevent row click
    navigate(`/edit-device/${device.id}`);
  };

  // Handle delete device click (show confirmation)
  // const handleDeleteDevice = (event: React.MouseEvent, device: Device) => {
  //   event.stopPropagation(); // Prevent row click
  //   setDeviceToDelete(device);
  //   setDeleteConfirmOpen(true);
  // };

  // Quick disable without confirmation
  const handleDisableNow = async (event: React.MouseEvent, device: Device) => {
    event.stopPropagation();
    try {
      const response = await axiosInstance.patch(
        `/gpstracker/devicehandler/${device.id}`,
        { disabled: true },
      );

      if (response.data.status) {
        fetchDevices();
      } else {
        console.error("Failed to disable device:", response.data.message);
      }
    } catch (err: any) {
      console.error("Error disabling device:", err);
    }
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;

    try {
      const response = await axiosInstance.patch(
        `/gpstracker/devicehandler/${deviceToDelete.id}`,
        {
          disabled: true,
        },
      );

      if (response.data.status) {
        console.log("Device disabled successfully:", response.data);
        fetchDevices();
      } else {
        console.error("Failed to disable device:", response.data.message);
        // You can add error notification here if needed
      }
    } catch (err: any) {
      console.error("Error disabling device:", err);
      // You can add error notification here if needed
    } finally {
      setDeleteConfirmOpen(false);
      setDeviceToDelete(null);
    }
  };

  // Handle cancel deletion
  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeviceToDelete(null);
  };

  // Handle speed limit click
  const handleSpeedLimitClick = (event: React.MouseEvent, device: Device) => {
    event.stopPropagation(); // Prevent row click
    setSpeedLimitDevice(device);
    setTempSpeedLimit(device.speed_limit?.toString() || "");
    setSpeedLimitModalOpen(true);
  };

  // Handle user connections click
  const handleUserConnectionsClick = (
    event: React.MouseEvent,
    device: Device,
  ) => {
    event.stopPropagation(); // Prevent row click
    setSelectedDeviceForUserConnections(device);
    setUserConnectionsModalOpen(true);
  };

  // Handle notification attach click
  const handleNotificationAttachClick = (
    event: React.MouseEvent,
    device: Device,
  ) => {
    event.stopPropagation();
    setSelectedDeviceForNotification(device);
    setSelectedNotificationId("");
    setNotificationDialogMessage({ type: null, text: "" });
    setAttachedNotifications([]);
    setNotificationModalOpen(true);
    fetchNotificationOptions();
    fetchAttachedNotifications(device.id);
  };

  // Fetch notification list
  const fetchNotificationOptions = async () => {
    setNotificationDialogLoading(true);

    try {
      const response = await axiosInstance.get(
        "/gpstracker/notificationhandler/?limit=1000&offset=0",
      );

      const allowedNotifications: { [key: string]: string } = {
        geofence_exit: "Geofence Exit",
        geofence_enter: "Geofence Enter",
        ignition_on: "Ignition ON",
        ignition_off: "Ignition OFF",
        speed_limit: "Speed Limit Exceeded",
        low_battery: "Low Battery",
        device_offline: "Offline",
        maintenance_due: "Maintenance Due",
        engine_cut: "engine_cut",
        fuel_theft: "fuel_theft",
        tampering: "tampering",
        overspeed: "speed_limit",
        idle: "idle",
        moving: "moving",
        stopped: "stopped",
        vibration_alarm: "vibration_alarm",
        sos_alarm: "sos_alarm",
        charging_complete: "charging_complete",
        charging_connected: "charging_connected",
        charging_disconnected: "charging_disconnected",
        battery_power: "battery_power",
      };

      const rawData = response.data?.responseData || [];

      const uniqueTypes = new Set<string>();
      const options: { id: number; label: string }[] = [];

      rawData.forEach((item: any) => {
        const type = item.notification_type;
        if (!type) return;

        const normalizedType = type.toLowerCase();
        // Check if the type exists in our allowed map directly or case-insensitively
        const matchedKey = Object.keys(allowedNotifications).find(
          (key) => key.toLowerCase() === normalizedType,
        );

        if (matchedKey && !uniqueTypes.has(matchedKey)) {
          uniqueTypes.add(matchedKey);
          options.push({
            id: item.id,
            label: allowedNotifications[matchedKey],
          });
        }
      });

      console.log("Filtered Options:", options);
      setNotificationOptions(options);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotificationOptions([]);
      setNotificationDialogMessage({
        type: "error",
        text: "Failed to load notifications. Please try again.",
      });
    } finally {
      setNotificationDialogLoading(false);
    }
  };

  // Fetch attached notifications for a device
  const fetchAttachedNotifications = async (deviceId: number) => {
    setAttachedNotificationsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/gpstracker/configurationhandler/?device_id=${deviceId}&offset=0&limit=100`,
      );
      const attachedConfigs = (response.data?.responseData || []).filter(
        (config: any) =>
          config.notification !== null && config.notification !== undefined,
      );
      setAttachedNotifications(attachedConfigs);
    } catch (error) {
      console.error("Error fetching attached notifications:", error);
      setAttachedNotifications([]);
    } finally {
      setAttachedNotificationsLoading(false);
    }
  };

  const handleAttachNotification = async () => {
    if (!selectedDeviceForNotification || !selectedNotificationId) return;

    setNotificationDialogLoading(true);
    setNotificationDialogMessage({ type: null, text: "" });
    try {
      await axiosInstance.post("/gpstracker/configurationhandler/", {
        user_id: null,
        device_id: selectedDeviceForNotification.id,
        group_id: null,
        geofence_id: null,
        notification_id: parseInt(selectedNotificationId, 10),
        calendar_id: null,
        attribute_id: null,
        driver_id: null,
        managed_user_id: null,
        command_id: null,
      });
      setNotificationDialogMessage({
        type: "success",
        text: "Notification attached to device successfully.",
      });
      // Refresh the attached notifications list after successful attachment
      if (selectedDeviceForNotification) {
        fetchAttachedNotifications(selectedDeviceForNotification.id);
      }
      setSelectedNotificationId("");
    } catch (error) {
      console.error("Error attaching notification:", error);
      setNotificationDialogMessage({
        type: "error",
        text: "Failed to attach notification. Please try again.",
      });
    } finally {
      setNotificationDialogLoading(false);
    }
  };

  // Handle delete attached notification
  const handleDeleteAttachedNotification = async (configurationId: number) => {
    try {
      await axiosInstance.delete(
        `/gpstracker/configurationhandler/${configurationId}`,
      );
      setNotificationDialogMessage({
        type: "success",
        text: "Notification detached successfully.",
      });
      // Refresh the attached notifications list
      if (selectedDeviceForNotification) {
        fetchAttachedNotifications(selectedDeviceForNotification.id);
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      setNotificationDialogMessage({
        type: "error",
        text: "Failed to detach notification. Please try again.",
      });
    }
  };

  // Handle closing user connections modal
  const handleCloseUserConnectionsModal = () => {
    setUserConnectionsModalOpen(false);
    setSelectedDeviceForUserConnections(null);
  };

  const handleCloseNotificationModal = () => {
    setNotificationModalOpen(false);
    setSelectedDeviceForNotification(null);
    setSelectedNotificationId("");
    setNotificationDialogMessage({ type: null, text: "" });
    setAttachedNotifications([]);
  };

  // Handle closing speed limit modal
  const handleCloseSpeedLimitModal = () => {
    setSpeedLimitModalOpen(false);
    setSpeedLimitDevice(null);
    setTempSpeedLimit("");
  };

  // Handle saving speed limit
  const handleSaveSpeedLimit = async () => {
    if (!speedLimitDevice) return;

    const speedLimitNum = parseInt(tempSpeedLimit);
    if (tempSpeedLimit.trim() === "") {
      // Show error message
      setError("Please enter a speed limit value");
      return;
    }
    if (isNaN(speedLimitNum) || speedLimitNum < 0 || speedLimitNum > 200) {
      setError("Please enter a valid speed limit between 0 and 200 km/h");
      return;
    }

    setIsSpeedLimitLoading(true);
    try {
      const response = await axiosInstance.patch(
        `/gpstracker/devicehandler/${speedLimitDevice.id}`,
        {
          ...speedLimitDevice,
          speed_limit: speedLimitNum,
        },
      );

      if (response.data.status) {
        // Update local state
        // const updatedDevices = devices.map((d: Device) => (d.id === speedLimitDevice.id ? { ...d, speed_limit: speedLimitNum } : d));
        // You might need to update your device context here depending on your implementation
        handleCloseSpeedLimitModal();
      } else {
        setError("Failed to update speed limit. Please try again.");
      }
    } catch (err: any) {
      console.error("Error updating speed limit:", err);
      setError("Failed to update speed limit. Please try again.");
    } finally {
      setIsSpeedLimitLoading(false);
    }
  };

  // Handle export to Excel
  const handleExportExcel = async () => {
    if (!canExport) {
      setError("You don't have permission to export data.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (filteredDevices.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const excelData = filteredDevices.map((device) => ({
        "Vehicle Name": device.name,
        "Device ID": device.unique_id,
        "Sim No.": device.sim_number || "N/A",
        MDN: device.attributes?.mdn_number || "N/A",
        "Customer Name": device.attributes.customer_name || "N/A",
        Model: device.model || "N/A",
        Phone: device.phone || "N/A",
        Category: device.category || "N/A",
        Status: getDeviceStatus(device),
        Ignition: device.attributes.ignition ? "ON" : "OFF",
        Power:
          device?.latest_heartbeat?.gps_data?.terminal_info?.alarm_status ===
            "Power Cut Alarm"
            ? "ON"
            : "OFF",
        "Battery Level": device.attributes.battery_level || "N/A",
        "Latest Speed": device.attributes.latest_speed || "0",
        "Activation Date": device.activated_on
          ? format(new Date(device.activated_on), "dd/MM/yyyy")
          : "N/A",
        "Expiration Date": device.expiration_time
          ? format(new Date(device.expiration_time), "dd/MM/yyyy")
          : "N/A",
        "GPS Position": device.latest_position?.gps_data
          ? `${device.latest_position.gps_data.latitude}, ${device.latest_position.gps_data.longitude}`
          : "N/A",
        "Last Update": device.latest_position?.updated_at
          ? format(device.latest_position.updated_at, "dd:MM:yyyy")
          : "N/A",
        "External Voltage":
          device.latest_heartbeat?.gps_data?.external_voltage_value || "N/A",
      }));

      setDownloadProgress(50);

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles List");

      setDownloadProgress(75);

      // Generate Excel file
      XLSX.writeFile(
        workbook,
        `Vehicles_List_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`,
      );

      setDownloadProgress(100);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setError("Failed to export vehicles list. Please try again.");
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  const fetchDevices = async () => {
    setDevicesLoading(true);
    setError(null);
    const statusParam = getStatusParam(selectedFilter);
    const hasCreatedDateFilter = Boolean(createdFrom || createdTo);

    try {
      const response = await axiosInstance.get("/gpstracker/devicehandler/", {
        params: {
          limit: hasCreatedDateFilter ? undefined : rowsPerPage,
          offset: hasCreatedDateFilter ? undefined : page * rowsPerPage,
          status: statusParam,
          created_at: createdFrom
            ? new Date(createdFrom).toISOString()
            : undefined,
            response_type: "new_response",
          device_name: searchQuery.trim() || undefined,
        },
      });

      if (response.data?.status && response.data?.responseData) {
        const responseData = response.data.responseData;
        setDevices(responseData.devices || []);
        const counts = responseData.counts || {};
        setStatistics({
          all: counts.total || 0,
          running: counts.running || 0,
          idle: counts.idle || 0,
          stopped: counts.stopped || 0,
          inactive: counts.inactive || 0,
          no_data: counts.no_data || 0,
          expired: counts.expired || 0,
        });
      } else {
        setDevices([]);
        setStatistics(null);
      }
    } catch (err: any) {
      console.error("Error fetching devices:", err);
      setDevices([]);
      setStatistics(null);
      setError("Failed to load vehicles. Please try again.");
    } finally {
      setDevicesLoading(false);
    }
  };

  // Handle filter changes from URL
  const handleFilterChange = (filterName: string) => {
    setSelectedFilter(filterName);
    setPage(0); // Reset to first page when filter changes

    if (filterName === "Total") {
      navigate("/vehicles", { replace: true });
    } else {
      const urlParam = getUrlFromFilter(filterName);
      navigate(`/vehicles/${urlParam}`, { replace: true });
    }
  };

  // Sync selectedFilter with URL parameter changes
  useEffect(() => {
    setSelectedFilter(getFilterFromUrl(filter));
    setPage(0); // Reset to first page when URL filter changes
  }, [filter]);

  useEffect(() => {
    fetchDevices();

    const intervalId = window.setInterval(() => {
      fetchDevices();
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, searchQuery, page, rowsPerPage, createdFrom, createdTo]);

  // Fetch geofencing data
  const fetchGeofencings = async () => {
    try {
      const response = await axiosInstance.get("/gpstracker/geofencehandler/", {
        params: {
          limit: 100000,
          offset: 0,
        },
      });

      if (response.data.status && response.data.responseData) {
        setGeofencings(response.data.responseData);
      } else {
        console.error("Failed to fetch geofences");
        setGeofencings([]);
      }
    } catch (err: any) {
      console.error("Failed to load geofencings:", err);
      setGeofencings([]);
    } finally {
    }
  };

  useEffect(() => {
    fetchGeofencings();
  }, []);

  // Helper function to determine device status
  const getDeviceStatus = (device: Device) => {
    if (
      !device?.latest_position?.updated_at &&
      !device?.latest_heartbeat?.updated_at
    ) {
      return "No Data";
    }

    const lastUpdateTime = Math.max(
      device?.latest_position?.updated_at || 0,
      device?.latest_heartbeat?.updated_at || 0,
    );
    const currentTime = Date.now();
    const timeDiffInHours = (currentTime - lastUpdateTime) / (1000 * 60 * 60);

    if (timeDiffInHours > 4) {
      return "Inactive";
    }

    if (
      device?.attributes?.latest_speed &&
      device.attributes.latest_speed > 0
    ) {
      return "Running";
    }
    if (device?.attributes?.status === "idle") {
      return "Idle";
    }
    if (device?.attributes?.status === "stopped") {
      return "Stopped";
    }
    if (device?.attributes?.status === "offline") {
      return "Inactive";
    }
    if (
      !device?.latest_position?.gps_data ||
      !device?.latest_position?.gps_data?.latitude ||
      !device?.latest_position?.gps_data?.longitude
    ) {
      return "No Data";
    }
    return "No Data";
  };

  // Helper function to format date with suffix
  const formatLastUpdatedAt = (timestamp: any) => {
    if (!timestamp) return "-";
    return format(new Date(timestamp), "d MMM h:mm a").toLowerCase();
  };

  const formatInactiveAging = (timestamp: any) => {
    if (!timestamp) return "-";
    const numericTimestamp = Number(timestamp);
    const normalizedTimestamp =
      !Number.isNaN(numericTimestamp) && numericTimestamp < 1000000000000
        ? numericTimestamp * 1000
        : timestamp;
    const date = new Date(normalizedTimestamp);
    if (Number.isNaN(date.getTime())) return "-";
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const getBatteryColor = (device: Device) => {
    const voltageLevelRaw = device?.latest_heartbeat?.gps_data?.voltage_level;
    const batteryPowerRaw = device?.latest_heartbeat?.gps_data?.battery_power;

    let percent: number | null = null;

    if (typeof voltageLevelRaw === "string") {
      const trimmed = voltageLevelRaw.trim();
      if (trimmed.endsWith("%")) {
        const numericPart = parseFloat(trimmed.slice(0, -1));
        if (!isNaN(numericPart)) {
          percent = numericPart;
        }
      }
    }

    if (percent === null) {
      const batteryNumeric = Number(batteryPowerRaw);
      if (!isNaN(batteryNumeric)) {
        percent = batteryNumeric;
      }
    }

    if (percent === null) {
      return {
        color: "#E53935",
        backgroundColor: "#FFEBEE",
      };
    }

    const isHealthy = percent > 10;
    return {
      color: isHealthy ? "#43A047" : "#E53935",
      backgroundColor: isHealthy ? "#E8F5E9" : "#FFEBEE",
    };
  };

  // When alarm_status is missing/undefined, default to gray styles
  const getPowerColorFromAlarmStatus = (alarmStatus?: string) => {
    if (!alarmStatus) {
      return {
        color: "#757575",
        backgroundColor: "#F5F5F5",
      };
    }
    return getPowerColor(alarmStatus === "Power Cut Alarm");
  };

  const filteredDevices = devices.filter((device: any) => {
    const deviceCreatedAt = device.created_at
      ? new Date(device.created_at)
      : null;

    // Frontend fallback filtering by created_at date range
    if (createdFrom) {
      const fromDate = new Date(createdFrom);
      if (!deviceCreatedAt || deviceCreatedAt < fromDate) return false;
    }
    if (createdTo) {
      const toDate = new Date(createdTo);
      toDate.setHours(23, 59, 59, 999);
      if (!deviceCreatedAt || deviceCreatedAt > toDate) return false;
    }

    return true;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Custom pagination actions with first/last page buttons
  interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
  }

  function TablePaginationActions(props: TablePaginationActionsProps) {
    const { count, page, rowsPerPage, onPageChange } = props;
    const muiTheme = useMuiTheme();

    const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </Box>
    );
  }

  // Debug logging for vehicle filtering
  React.useEffect(() => {
    console.log("🚗 Vehicle API Debug:");
    console.log(`📊 Devices from API: ${devices.length}`);
    console.log(`📌 Current status filter: ${selectedFilter}`);
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  }, [devices, selectedFilter]);

  const totalCount = (() => {
    if (!statistics) return 0;
    if (selectedFilter === "Total") return statistics.all;
    const statusParam = getStatusParam(selectedFilter);
    if (!statusParam) return statistics.all;
    const keyMap: { [key: string]: keyof Statistics } = {
      running: "running",
      idle: "idle",
      stopped: "stopped",
      inactive: "inactive",
      expired: "expired",
      no_data: "no_data",
    };
    return statistics[keyMap[statusParam]] || 0;
  })();

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "running":
        return "#43A047"; // Green
      case "stopped":
        return "#E53935"; // Red
      case "idle":
        return "#FFA000"; // Orange
      case "inactive":
        return "#1976D2"; // Blue
      default:
        return "#757575"; // Gray for other statuses
    }
  };

  const getIgnitionColor = (ignition: boolean) => {
    return {
      color: ignition ? "#43A047" : "#b40000",
      backgroundColor: ignition ? "#E8F5E9" : "#fed8d8",
    };
  };

  const getPowerColor = (charge: boolean) => {
    return {
      color: charge ? "#E53935" : "#43A047",
      backgroundColor: charge ? "#FFEBEE" : "#E8F5E9",
    };
  };

  const getGpsColor = (device: Device) => {
    if (!device?.latest_position?.gps_data) {
      return {
        color: "#757575",
        backgroundColor: "#F5F5F5",
      };
    }

    const heartbeatTime = device?.latest_heartbeat?.updated_at || 0;
    const positionTime = device?.latest_position?.updated_at || 0;

    if (heartbeatTime > positionTime) {
      return {
        color: "#E53935",
        backgroundColor: "#FFEBEE",
      };
    }

    return {
      color: "#43A047",
      backgroundColor: "#E8F5E9",
    };
  };

  const getVoltageColor = (device: Device) => {
    const hasVoltage =
      device?.latest_heartbeat?.gps_data?.external_voltage_value;
    return {
      color: hasVoltage ? "#43A047" : "#E53935",
      backgroundColor: hasVoltage ? "#E8F5E9" : "#FFEBEE",
    };
  };

  const getVoltageLabel = (device: Device) => {
    return `${device?.latest_heartbeat?.gps_data?.external_voltage_value?.toFixed(1) || "0"}V`;
  };

  const getLockColor = (device: Device) => {
    if (!device?.latest_position && !device?.latest_heartbeat) {
      return {
        color: "#757575",
        backgroundColor: "#F5F5F5",
      };
    }

    const isDisconnected =
      device?.latest_heartbeat?.gps_data?.terminal_info
        ?.oil_electricity_disconnected;

    if (isDisconnected) {
      return {
        color: "#E53935",
        backgroundColor: "#FFEBEE",
      };
    }

    return {
      color: "#43A047",
      backgroundColor: "#E8F5E9",
    };
  };

  const getLockLabel = (device: Device) => {
    if (!device?.latest_position && !device?.latest_heartbeat) {
      return "No Data";
    }

    const isLabelDisconnected =
      device?.latest_heartbeat?.gps_data?.terminal_info
        ?.oil_electricity_disconnected;
    return isLabelDisconnected ? "Locked" : "Unlocked";
  };

  const getLockIcon = (blocked: boolean) => {
    const isIconDisconnected = blocked;
    return isIconDisconnected ? (
      <LockIcon sx={{ fontSize: 10 }} />
    ) : (
      <UnlockIcon sx={{ fontSize: 10 }} />
    );
  };

  // Helper: feature availability based on device.attributes.port (mirrors mobile logic)
  const isFeatureEnabled = (device: any, featureKey: string): boolean => {
    const port = device?.attributes?.port;
    if (!port) return false;
    return port[featureKey] === true;
  };

  // Skeleton component for statistics cards
  const StatisticsSkeleton = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(7, 1fr)",
        },
        gap: 2,
      }}
    >
      {[...Array(7)].map((_, index) => (
        <Card key={index} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 2.5, textAlign: "center" }}>
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ mx: "auto", mb: 1.5 }}
            />
            <Skeleton
              variant="text"
              width="60%"
              height={32}
              sx={{ mx: "auto", mb: 0.5 }}
            />
            <Skeleton
              variant="text"
              width="80%"
              height={16}
              sx={{ mx: "auto" }}
            />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Skeleton component for table rows
  const TableRowSkeleton = ({ index }: { index: number }) => (
    <Box
      key={index}
      sx={{
        display: "grid",
        gridTemplateColumns:
          "60px 210px 130px 130px 130px 110px 70px 70px 70px 80px 70px 110px",
        gap: 2,
        px: 3,
        py: 2.5,
        alignItems: "center",
        minHeight: 72,
        borderBottom: "1px solid #f1f5f9",
      }}
    >
      <Skeleton variant="circular" width={32} height={32} />
      <Box>
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="60%" height={16} />
      </Box>
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton
        variant="rectangular"
        width={120}
        height={28}
        sx={{ borderRadius: 2 }}
      />
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
      <Skeleton variant="circular" width={36} height={36} />
    </Box>
  );

  if (devicesLoading && devices.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: alpha(getPrimaryColor(), 0.02),
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          {/* Statistics Cards Skeleton */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 1,
              borderRadius: 4,
              bgcolor: "white",
              border: "1px solid",
              borderColor: alpha(getPrimaryColor(), 0.2),
            }}
          >
            <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
            <StatisticsSkeleton />
          </Paper>

          {/* Search Section Skeleton */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 4,
              bgcolor: "white",
              border: "1px solid",
              borderColor: alpha(getPrimaryColor(), 0.2),
            }}
          >
            <Skeleton
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 3, mb: 3 }}
            />
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
              {[...Array(7)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={100}
                  height={32}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
            <Skeleton variant="text" width={300} height={20} />
          </Paper>

          {/* Table Skeleton */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid",
              borderColor: alpha(getPrimaryColor(), 0.2),
              overflow: "hidden",
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "60px 210px 130px 130px 130px 110px 70px 70px 70px 80px 70px 110px",
                gap: 2,
                px: 3,
                py: 2.5,
                bgcolor: alpha(getPrimaryColor(), 0.05),
                borderBottom: `1px solid ${alpha(getPrimaryColor(), 0.2)}`,
                alignItems: "center",
                minHeight: 64,
              }}
            >
              {[...Array(12)].map((_, index) => (
                <Skeleton key={index} variant="text" width="70%" height={16} />
              ))}
            </Box>

            {/* Table Body Skeleton */}
            <Box>
              {[...Array(8)].map((_, index) => (
                <TableRowSkeleton key={index} index={index} />
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error && devices.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{
            mt: 2,
            borderRadius: 3,
            boxShadow: muiTheme.shadows[1],
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  console.log("filteredDevices", filteredDevices);

  return (
    <Box
      sx={{
        bgcolor: alpha(getPrimaryColor(), 0.02),
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Professional Header */}
        <Box
          sx={{
            mb: 1,
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: 1,
            py: 1,
            px: 0,
          }}
        >
          {/* Search */}
          <Tooltip
            title="Search vehicles by name, ID, or phone number"
            arrow
            placement="bottom"
          >
            <TextField
              placeholder="Search vehicles..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF", fontSize: 16 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSearchQuery("")}
                      size="small"
                      sx={{
                        color: "#9CA3AF",
                        "&:hover": {
                          backgroundColor: "#F3F4F6",
                          color: "#6B7280",
                        },
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 250 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  bgcolor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  height: 36,
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                  },
                  "&:hover": {
                    borderColor: "#D1D5DB",
                    bgcolor: "#FFFFFF",
                  },
                  "&.Mui-focused": {
                    borderColor: getPrimaryColor(),
                    boxShadow: `0 0 0 3px ${alpha(getPrimaryColor(), 0.1)}`,
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />
          </Tooltip>

          {/* Status Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: { xs: "auto", md: "visible" },
              pb: { xs: 0.5, md: 0 },
              "&::-webkit-scrollbar": { height: 6 },
            }}
          >
            {[
              {
                key: "all",
                label: "Total",
                bgColor: getPrimaryColor(),
                description: "Show all vehicles",
              },
              {
                key: "running",
                label: "Running",
                bgColor: "#43A047",
                description: "Vehicles currently in motion",
              },
              {
                key: "idle",
                label: "Idle",
                bgColor: "#FFA000",
                description: "Vehicles with engine running but stationary",
              },
              {
                key: "stopped",
                label: "Stopped",
                bgColor: "#D32F2F",
                description: "Vehicles with engine off",
              },
              {
                key: "inactive",
                label: "Inactive",
                bgColor: "#0284F5",
                description: "Vehicles with no recent activity",
              },
              {
                key: "expired",
                label: "Expired",
                bgColor: "#e65100",
                description: "Vehicles with expired subscriptions",
              },
              {
                key: "no_data",
                label: "No Data",
                bgColor: "#757575",
                description: "Vehicles with no GPS or heartbeat data",
              },
            ].map((filter) => {
              const count = statistics
                ? statistics[filter.key as keyof Statistics] || 0
                : 0;
              const isSelected = selectedFilter === filter.label;

              return (
                <Tooltip
                  key={filter.key}
                  title={filter.description}
                  arrow
                  placement="bottom"
                >
                  <Button
                    onClick={() => handleFilterChange(filter.label)}
                    variant="contained"
                    size="small"
                    sx={{
                      flexShrink: 0,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textTransform: "none",
                      minWidth: "auto",
                      color: "#FFFFFF",
                      backgroundColor: isSelected
                        ? filter.bgColor
                        : filter.bgColor,
                      border: "none",
                      boxShadow: isSelected
                        ? `0 0 8px ${alpha(filter.bgColor, 0.6)}`
                        : "0 1px 3px rgba(0,0,0,0.2)",
                      "&:hover": {
                        backgroundColor: filter.bgColor,
                        color: "#FFFFFF",
                        transform: "translateY(-1px)",
                        boxShadow: `0 0 12px ${alpha(filter.bgColor, 0.8)}, 0 2px 6px rgba(0,0,0,0.3)`,
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {filter.label} ({count})
                  </Button>
                </Tooltip>
              );
            })}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              ml: { xs: 0, md: "auto" },
              width: { xs: "100%", md: "auto" },
              flexWrap: "wrap",
            }}
          >
            <Tooltip title="View inventory vehicles" arrow placement="bottom">
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/inventory")}
                sx={{
                  width: { xs: "calc(50% - 4px)", sm: "auto" },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  backgroundColor: "#8B5CF6",
                  color: "#FFFFFF",
                  border: "none",
                  boxShadow: "0 0 3px rgba(139, 92, 247, 0.3)",
                  "&:hover": {
                    backgroundColor: "#7B46C7",
                    boxShadow: "0 0 8px rgba(139, 92, 247, 0.5)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Inventory
              </Button>
            </Tooltip>

            <Tooltip title="Add a new vehicle" arrow placement="bottom">
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/add-device")}
                sx={{
                  width: { xs: "calc(50% - 4px)", sm: "auto" },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  backgroundColor: getPrimaryColor(),
                  "&:hover": {
                    backgroundColor: getPrimaryLightColor(),
                  },
                }}
              >
                Add Vehicle
              </Button>
            </Tooltip>

            <Tooltip
              title="Bulk add multiple vehicles"
              arrow
              placement="bottom"
            >
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/bulk-add")}
                sx={{
                  width: { xs: "calc(50% - 4px)", sm: "auto" },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  backgroundColor: "#000000",
                  "&:hover": {
                    backgroundColor: "#000000",
                  },
                }}
              >
                Bulk
              </Button>
            </Tooltip>

            <Tooltip title="Filter by created date range" arrow placement="bottom">
              <Button
              disabled
                variant="contained"
                fullWidth
                onClick={handleOpenBulkPopover}
                sx={{
                  width: { xs: "calc(50% - 4px)", sm: "auto" },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  backgroundColor: "#111827",
                  "&:hover": {
                    backgroundColor: "#111827",
                  },
                }}
              >
                Date Filter
              </Button>
            </Tooltip>
            <Popover
              open={Boolean(bulkAnchorEl)}
              anchorEl={bulkAnchorEl}
              onClose={handleCloseBulkPopover}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { p: 2, width: 360 } }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Filter by Created Date
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={createdFrom}
                    onChange={(e) => {
                      setCreatedFrom(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={createdTo}
                    onChange={(e) => {
                      setCreatedTo(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setCreatedFrom("");
                      setCreatedTo("");
                      setPage(0);
                    }}
                  >
                    Clear
                  </Button>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* <Button
                      variant='contained'
                      size='small'
                      onClick={handleCloseBulkPopover}
                    >
                      Apply
                    </Button> */}
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Box>

        {/* Vehicle Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: alpha(getPrimaryColor(), 0.2),
            overflow: "hidden",
            bgcolor: "white",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "20px 210px 100px 100px 80px 50px 50px 50px 70px 60px 60px 60px 60px 80px 170px 150px",
                gap: 1,
                px: 1.5,
                py: 1,
                bgcolor: alpha(getPrimaryColor(), 0.05),
                borderBottom: `1px solid ${alpha(getPrimaryColor(), 0.2)}`,
                alignItems: "center",
                minHeight: 40,
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                #
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "left" }}
              >
                VEHICLE DETAILS
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "left" }}
              >
                MODEL
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "left" }}
              >
                MDN
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                STATUS
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                PWR
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                IGN
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                GPS
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                VOLTAGE
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                BATTERY
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                LOCK
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                SPEED
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                ACT DATE
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                EXP DATE
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "center" }}
              >
                ACTION
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="#374151"
                sx={{ fontSize: "0.7rem", textAlign: "start" }}
              >
                ADDRESS
              </Typography>
            </Box>

            {/* Table Body */}
            <Box>
              {filteredDevices.map((device, index) => (
                <Box
                  key={device.id}
                  onClick={() => handleRowClick(device.id)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "20px 210px 100px 100px 80px 50px 50px 50px 70px 60px 60px 60px 60px 80px 170px 150px",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    alignItems: "center",
                    minHeight: 48,
                    borderBottom: `1px solid ${alpha(getPrimaryColor(), 0.1)}`,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    width: "100%",
                    "&:hover": {
                      bgcolor: alpha(getPrimaryColor(), 0.05),
                    },
                  }}
                >
                  {/* Row Number */}
                  <Typography
                    variant="caption"
                    color="#6b7280"
                    fontWeight={500}
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {page * rowsPerPage + index + 1}
                  </Typography>

                  {/* Vehicle Details */}
                  <Box>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#1f2937"
                      sx={{ fontSize: "0.7rem", mb: 0.2, display: "block" }}
                    >
                      {device.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="#6b7280"
                      sx={{ fontSize: "0.65rem", display: "block", mb: 0.2 }}
                    >
                      ID: {device.unique_id}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="#6b7280"
                      sx={{ fontSize: "0.65rem", display: "block" }}
                    >
                      SIM No.: {device.sim_number || "-"}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="#6b7280"
                      sx={{
                        fontSize: "0.65rem",
                        display: "block",
                        color: "red",
                      }}
                    >
                      Customer Name: {device.attributes.customer_name || "-"}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="#6b7280"
                      sx={{ fontSize: "0.65rem", textTransform: "uppercase" }}
                    >
                      {device.latest_heartbeat === null
                        ? "No Data"
                        : formatLastUpdatedAt(
                          device?.attributes?.last_updated_at,
                        )}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="#6b7280"
                      sx={{ fontSize: "0.65rem", display: "block" }}
                    >
                      {formatInactiveAging(device?.attributes?.last_updated_at)}
                    </Typography>
                  </Box>

                  {/* Model */}
                  <Typography
                    variant="caption"
                    color="#374151"
                    fontWeight={500}
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {device.model || "N/A"}
                  </Typography>

                  {/* MDN */}
                  <Typography
                    variant="caption"
                    color="#374151"
                    fontWeight={500}
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {device.attributes?.mdn_number || "N/A"}
                  </Typography>

                  {/* Status */}
                  <Tooltip
                    title={`Vehicle Status: ${getDeviceStatus(device)}`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${getStatusColor(getDeviceStatus(device))} 0%, ${alpha(
                          getStatusColor(getDeviceStatus(device)),
                          0.8,
                        )} 100%)`,
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        height: 20,
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 1px 4px ${alpha(getStatusColor(getDeviceStatus(device)), 0.3)}`,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        cursor: "pointer",
                      }}
                    >
                      {getDeviceStatus(device)}
                    </Box>
                  </Tooltip>

                  {/* Power */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {isFeatureEnabled(device, "power") &&
                      (() => {
                        const isNoData = getDeviceStatus(device) === "No Data";
                        const powerColor = isNoData
                          ? { color: "#757575", backgroundColor: "#F5F5F5" }
                          : getPowerColorFromAlarmStatus(
                            device?.latest_heartbeat?.gps_data?.terminal_info
                              ?.alarm_status,
                          );
                        return (
                          <Tooltip
                            title={`Power: ${device?.latest_heartbeat?.gps_data?.terminal_info
                              ?.alarm_status === "Power Cut Alarm"
                              ? "Power Cut Alarm"
                              : "Power Restored"
                              }`}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `linear-gradient(135deg, ${powerColor.backgroundColor} 0%, ${alpha(powerColor.color, 0.1)} 100%)`,
                                border: "1px solid",
                                borderColor: alpha(powerColor.color, 0.3),
                                boxShadow: `0 1px 4px ${alpha(powerColor.color, 0.25)}`,
                                color: powerColor.color,
                                cursor: "pointer",
                              }}
                            >
                              <PowerIcon sx={{ fontSize: 12 }} />
                            </Box>
                          </Tooltip>
                        );
                      })()}
                  </Box>

                  {/* Ignition */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {isFeatureEnabled(device, "ignition") &&
                      (() => {
                        const isNoData = getDeviceStatus(device) === "No Data";
                        const ignitionColor = isNoData
                          ? { color: "#757575", backgroundColor: "#F5F5F5" }
                          : getIgnitionColor(device.attributes.ignition);
                        return (
                          <Tooltip
                            title={`Ignition: ${device.attributes.ignition ? "ON" : "OFF"}`}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `linear-gradient(135deg, ${ignitionColor.backgroundColor} 0%, ${alpha(
                                  ignitionColor.color,
                                  0.1,
                                )} 100%)`,
                                border: "1px solid",
                                borderColor: alpha(ignitionColor.color, 0.3),
                                boxShadow: `0 1px 4px ${alpha(ignitionColor.color, 0.25)}`,
                                color: ignitionColor.color,
                                cursor: "pointer",
                              }}
                            >
                              <VpnKeyIcon sx={{ fontSize: 12 }} />
                            </Box>
                          </Tooltip>
                        );
                      })()}
                  </Box>

                  {/* GPS */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {(() => {
                      const isNoData = getDeviceStatus(device) === "No Data";
                      const gpsColor = isNoData
                        ? { color: "#757575", backgroundColor: "#F5F5F5" }
                        : getGpsColor(device);
                      return (
                        <Tooltip
                          title={`GPS Signal: ${device.latest_position?.gps_data ? "Connected" : "No Signal"}`}
                          arrow
                          placement="top"
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: `linear-gradient(135deg, ${gpsColor.backgroundColor} 0%, ${alpha(gpsColor.color, 0.1)} 100%)`,
                              border: "1px solid",
                              borderColor: alpha(gpsColor.color, 0.3),
                              boxShadow: `0 1px 4px ${alpha(gpsColor.color, 0.25)}`,
                              color: gpsColor.color,
                              cursor: "pointer",
                            }}
                          >
                            <WifiIcon sx={{ fontSize: 12 }} />
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  </Box>

                  {/* Voltage */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {isFeatureEnabled(device, "externalBattery") &&
                      (() => {
                        const isNoData = getDeviceStatus(device) === "No Data";
                        const voltageColor = isNoData
                          ? { color: "#757575", backgroundColor: "#F5F5F5" }
                          : getVoltageColor(device);
                        return (
                          <Tooltip
                            title={`External Voltage: ${getVoltageLabel(device)}`}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `linear-gradient(135deg, ${voltageColor.backgroundColor} 0%, ${alpha(
                                  voltageColor.color,
                                  0.1,
                                )} 100%)`,
                                border: "1px solid",
                                borderColor: alpha(voltageColor.color, 0.3),
                                boxShadow: `0 1px 4px ${alpha(voltageColor.color, 0.25)}`,
                                color: voltageColor.color,
                                fontSize: "0.6rem",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {getVoltageLabel(device)}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                  </Box>

                  {/* Battery */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {isFeatureEnabled(device, "internalBattery") &&
                      (() => {
                        const isNoData = getDeviceStatus(device) === "No Data";
                        const batteryColor = isNoData
                          ? { color: "#757575", backgroundColor: "#F5F5F5" }
                          : getBatteryColor(device);
                        return (
                          <Tooltip
                            title={`Battery Level: ${typeof device?.latest_heartbeat?.gps_data
                              ?.voltage_level === "string" &&
                              device?.latest_heartbeat?.gps_data?.voltage_level
                                .trim()
                                .endsWith("%")
                              ? device?.latest_heartbeat?.gps_data?.voltage_level.trim()
                              : `${(device?.latest_heartbeat?.gps_data?.voltage_level || device?.latest_heartbeat?.gps_data?.battery_power) ?? 0}%`
                              }`}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `linear-gradient(135deg, ${batteryColor.backgroundColor} 0%, ${alpha(
                                  batteryColor.color,
                                  0.1,
                                )} 100%)`,
                                border: "1px solid",
                                borderColor: alpha(batteryColor.color, 0.3),
                                boxShadow: `0 1px 4px ${alpha(batteryColor.color, 0.25)}`,
                                color: batteryColor.color,
                                fontSize: "0.6rem",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {typeof device?.latest_heartbeat?.gps_data
                                ?.voltage_level === "string" &&
                                device?.latest_heartbeat?.gps_data?.voltage_level
                                  .trim()
                                  .endsWith("%")
                                ? device?.latest_heartbeat?.gps_data?.voltage_level.trim()
                                : `${device?.latest_heartbeat?.gps_data?.battery_power ?? 0}%`}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                  </Box>

                  {/* Lock/Unlock */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {isFeatureEnabled(device, "immobilizer") &&
                      (() => {
                        const isNoData = getDeviceStatus(device) === "No Data";
                        const lockColor = isNoData
                          ? { color: "#757575", backgroundColor: "#F5F5F5" }
                          : getLockColor(device);
                        return (
                          <Tooltip
                            title={`Oil/Electricity: ${getLockLabel(device)}`}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `linear-gradient(135deg, ${lockColor.backgroundColor} 0%, ${alpha(lockColor.color, 0.1)} 100%)`,
                                border: "1px solid",
                                borderColor: alpha(lockColor.color, 0.3),
                                boxShadow: `0 1px 4px ${alpha(lockColor.color, 0.25)}`,
                                color: lockColor.color,
                                cursor: "pointer",
                              }}
                            >
                              {getLockIcon(device.attributes.blocked)}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                  </Box>

                  {/* Speed */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip
                      title={`Current Speed: ${device.attributes.latest_speed || 0} km/h`}
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="caption"
                        color="#374151"
                        fontWeight={600}
                        sx={{
                          fontSize: "0.7rem",
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: alpha(getPrimaryColor(), 0.05),
                          },
                        }}
                      >
                        {device.attributes.latest_speed
                          ? `${device.attributes.latest_speed} km/h`
                          : "0 km/h"}
                      </Typography>
                    </Tooltip>
                  </Box>

                  {/* Act Date */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip
                      title={`Activation Date: ${device.activated_on ? format(new Date(device.activated_on), "dd/MM/yyyy") : "N/A"}`}
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="caption"
                        color="#374151"
                        fontWeight={600}
                        sx={{
                          fontSize: "0.7rem",
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: alpha(getPrimaryColor(), 0.05),
                          },
                        }}
                      >
                        {device.activated_on
                          ? format(new Date(device.activated_on), "dd/MM/yyyy")
                          : "N/A"}
                      </Typography>
                    </Tooltip>
                  </Box>

                  {/* Exp Date */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip
                      title={`Expiration Date: ${device.expiration_time ? format(new Date(device.expiration_time), "dd/MM/yyyy") : "N/A"}`}
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="caption"
                        color="#374151"
                        fontWeight={600}
                        sx={{
                          fontSize: "0.7rem",
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: alpha(getPrimaryColor(), 0.05),
                          },
                        }}
                      >
                        {device.expiration_time
                          ? format(
                            new Date(device.expiration_time),
                            "dd/MM/yyyy",
                          )
                          : "N/A"}
                      </Typography>
                    </Tooltip>
                  </Box>

                  {/* Actions */}
                  <Box
                    sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
                  >
                    <Tooltip title="Edit Vehicle" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => handleEditDevice(e, device)}
                        sx={{
                          width: 20,
                          height: 20,
                          background: `linear-gradient(135deg, ${alpha(getPrimaryColor(), 0.1)} 0%, ${alpha(getPrimaryColor(), 0.05)} 100%)`,
                          border: "1px solid",
                          borderColor: alpha(getPrimaryColor(), 0.3),
                          color: getPrimaryColor(),
                          "&:hover": {
                            background: `linear-gradient(135deg, ${alpha(getPrimaryColor(), 0.2)} 0%, ${alpha(getPrimaryColor(), 0.1)} 100%)`,
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Disable Vehicle (quick)" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDisableNow(e, device)}
                        sx={{
                          width: 20,
                          height: 20,
                          background: `linear-gradient(135deg, ${alpha("#E53935", 0.1)} 0%, ${alpha("#E53935", 0.05)} 100%)`,
                          border: "1px solid",
                          borderColor: alpha("#E53935", 0.3),
                          color: "#E53935",
                          "&:hover": {
                            background: `linear-gradient(135deg, ${alpha("#E53935", 0.2)} 0%, ${alpha("#E53935", 0.1)} 100%)`,
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Set Speed Limit" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => handleSpeedLimitClick(e, device)}
                        sx={{
                          width: 20,
                          height: 20,
                          background: `linear-gradient(135deg, ${alpha(getPrimaryLightColor(), 0.1)} 0%, ${alpha(getPrimaryLightColor(), 0.05)} 100%)`,
                          border: "1px solid",
                          borderColor: alpha(getPrimaryLightColor(), 0.3),
                          color: getPrimaryLightColor(),
                          "&:hover": {
                            background: `linear-gradient(135deg, ${alpha(getPrimaryLightColor(), 0.2)} 0%, ${alpha(getPrimaryLightColor(), 0.1)} 100%)`,
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <SpeedIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Attach Notification" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          handleNotificationAttachClick(e, device)
                        }
                        sx={{
                          width: 20,
                          height: 20,
                          background: `linear-gradient(135deg, ${alpha("#2563EB", 0.1)} 0%, ${alpha("#2563EB", 0.05)} 100%)`,
                          border: "1px solid",
                          borderColor: alpha("#2563EB", 0.3),
                          color: "#2563EB",
                          "&:hover": {
                            background: `linear-gradient(135deg, ${alpha("#2563EB", 0.2)} 0%, ${alpha("#2563EB", 0.1)} 100%)`,
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <NotificationsActiveIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Manage User Connections" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => handleUserConnectionsClick(e, device)}
                        sx={{
                          width: 20,
                          height: 20,
                          background: `linear-gradient(135deg, ${alpha(getPrimaryDarkColor(), 0.1)} 0%, ${alpha(getPrimaryDarkColor(), 0.05)} 100%)`,
                          border: "1px solid",
                          borderColor: alpha(getPrimaryDarkColor(), 0.3),
                          color: getPrimaryDarkColor(),
                          "&:hover": {
                            background: `linear-gradient(135deg, ${alpha(getPrimaryDarkColor(), 0.2)} 0%, ${alpha(getPrimaryDarkColor(), 0.1)} 100%)`,
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Address */}
                  <Box>
                    <Tooltip
                      title={
                        device.latest_position?.gps_data?.address ||
                        "No GPS location data available"
                      }
                      arrow
                      placement="top"
                      sx={{ maxWidth: 300 }}
                    >
                      <Typography
                        variant="caption"
                        color="#374151"
                        fontWeight={500}
                        sx={{
                          fontSize: "0.75rem",
                          display: "block",
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            bgcolor: alpha(getPrimaryColor(), 0.05),
                          },
                        }}
                      >
                        {device.latest_position?.gps_data
                          ? `${device?.latest_position?.gps_data?.address}`
                          : "No GPS Data"}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            sx={{
              borderTop: `1px solid ${alpha(getPrimaryColor(), 0.2)}`,
              bgcolor: alpha(getPrimaryColor(), 0.02),
            }}
          />
        </Paper>
        {devicesLoading && (
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "#e2e8f0",
              bgcolor: "white",
            }}
          >
            <Typography
              variant="h5"
              color="text.secondary"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Loading
            </Typography>
            <PulseLoader color={getPrimaryColor()} size={10} />
          </Paper>
        )}
        {/* No Results Message */}
        {filteredDevices.length === 0 && !devicesLoading && (
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "#e2e8f0",
              bgcolor: "white",
            }}
          >
            <Typography
              variant="h5"
              color="text.secondary"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              No vehicles found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchQuery
                ? `No vehicles match your search "${searchQuery}"`
                : `No vehicles with status "${selectedFilter === "Total" ? "All" : selectedFilter}"`}
            </Typography>
          </Paper>
        )}

        {/* Add Geofencing Modal */}
        <Dialog
          open={geofencingModalOpen}
          onClose={handleCloseGeofencingModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={600}>
              Add Geofencing
            </Typography>
            {selectedDevice && (
              <Typography variant="body2" color="text.secondary">
                Device: {selectedDevice.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Select Geofencing</InputLabel>
              <Select
                value={selectedGeofencing}
                label="Select Geofencing"
                onChange={(e) => setSelectedGeofencing(e.target.value)}
              >
                {geofencings.map((geofencing) => (
                  <MenuItem key={geofencing.id} value={geofencing.id}>
                    {geofencing.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseGeofencingModal} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedGeofencing}
              onClick={handleAddGeofencing}
              sx={{
                backgroundColor: getPrimaryColor(),
                "&:hover": {
                  backgroundColor: getPrimaryLightColor(),
                },
              }}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Attach Modal */}
        <Dialog
          open={notificationModalOpen}
          onClose={handleCloseNotificationModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={600}>
              Attach Notification
            </Typography>
            {selectedDeviceForNotification && (
              <Typography variant="body2" color="text.secondary">
                Device: {selectedDeviceForNotification.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {notificationDialogMessage.type && (
              <Alert
                severity={notificationDialogMessage.type}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                {notificationDialogMessage.text}
              </Alert>
            )}

            {/* Attached Notifications List */}
            {attachedNotifications.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: alpha(getPrimaryColor(), 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(getPrimaryColor(), 0.2)}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5, color: getPrimaryColor() }}
                >
                  Already Attached Notifications ({attachedNotifications.length}
                  )
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {attachedNotifications.map((notification) => {
                    const notificationName =
                      notificationOptions.find(
                        (opt) => opt.id === notification.notification,
                      )?.label || `Notification ${notification.notification}`;
                    const formattedName = notificationName
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase());
                    return (
                      <Box
                        key={notification.id}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          p: "6px 10px",
                          backgroundColor: "white",
                          border: `1px solid ${alpha(getPrimaryColor(), 0.2)}`,
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formattedName}
                        </Typography>
                        <Tooltip title="Delete notification">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteAttachedNotification(notification.id)
                            }
                            sx={{
                              padding: "2px",
                              color: "#ef4444",
                              "&:hover": {
                                backgroundColor: alpha("#ef4444", 0.1),
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {attachedNotificationsLoading && (
              <Box
                sx={{ display: "flex", justifyContent: "center", py: 2, mb: 2 }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            <Autocomplete<{ id: number; label: string }>
              options={notificationOptions}
              getOptionLabel={(option) => option.label}
              loading={notificationDialogLoading}
              value={
                notificationOptions.find(
                  (opt) => opt.id.toString() === selectedNotificationId,
                ) || null
              }
              onChange={(_, newValue: { id: number; label: string } | null) => {
                setSelectedNotificationId(
                  newValue ? newValue.id.toString() : "",
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select notification"
                  placeholder="Search or select notification"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {notificationDialogLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#f9fafb",
                      "&:hover": { bgcolor: "#f3f4f6" },
                      "&.Mui-focused": {
                        bgcolor: "white",
                        boxShadow: `0 0 0 2px ${alpha(getPrimaryColor(), 0.2)}`,
                      },
                    },
                  }}
                />
              )}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseNotificationModal} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              disabled={!selectedNotificationId || notificationDialogLoading}
              onClick={handleAttachNotification}
              sx={{
                backgroundColor: getPrimaryColor(),
                "&:hover": {
                  backgroundColor: getPrimaryLightColor(),
                },
              }}
            >
              {notificationDialogLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Attach Notification"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: alpha("#E53935", 0.12),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WarningIcon sx={{ color: "#E53935", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  Delete Device
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This action cannot be undone
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {deviceToDelete && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to delete the following device?
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {deviceToDelete.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {deviceToDelete.unique_id}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Model: {deviceToDelete.model || "N/A"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  The device will be disabled and removed from the active list.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCancelDelete} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              sx={{
                bgcolor: "#E53935",
                "&:hover": {
                  bgcolor: "#C62828",
                },
              }}
            >
              Delete Device
            </Button>
          </DialogActions>
        </Dialog>

        {/* Speed Limit Modal */}
        <Dialog
          open={speedLimitModalOpen}
          onClose={handleCloseSpeedLimitModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={600}>
              Set Speed Limit
            </Typography>
            {speedLimitDevice && (
              <Typography variant="body2" color="text.secondary">
                Device: {speedLimitDevice.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <TextField
                type="number"
                value={tempSpeedLimit}
                onChange={(e) => setTempSpeedLimit(e.target.value)}
                placeholder="Speed Limit (km/h)"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">km/h</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: "#f9fafb",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#f3f4f6",
                    },
                    "&.Mui-focused": {
                      bgcolor: "white",
                      boxShadow: `0 0 0 2px ${alpha(getPrimaryColor(), 0.2)}`,
                    },
                  },
                }}
              />
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseSpeedLimitModal} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isSpeedLimitLoading}
              onClick={handleSaveSpeedLimit}
              sx={{
                backgroundColor: getPrimaryColor(),
                "&:hover": {
                  backgroundColor: getPrimaryLightColor(),
                },
              }}
            >
              {isSpeedLimitLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save Speed Limit"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Device User Connections Modal */}
        <DeviceUserConnectionsForm
          id={selectedDeviceForUserConnections?.id?.toString() || null}
          open={userConnectionsModalOpen}
          onClose={handleCloseUserConnectionsModal}
        />
      </Container>

      {/* Download Progress Dialog */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={downloading}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <CircularProgress
            variant="determinate"
            value={downloadProgress}
            size={60}
            thickness={4}
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" color="text.primary">
            {downloadProgress < 100
              ? "Preparing Download..."
              : "Download Complete!"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {downloadProgress}% Complete
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default DeviceCards;
